import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { parseMarkdownLinks } from "../utils/parseMarkdownLinks";

const HomePageAdCarousel = ({ ads, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-rotate ads every 4 seconds
  useEffect(() => {
    if (ads.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % ads.length);
    }, 7000);

    return () => clearInterval(interval);
  }, [ads.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % ads.length);
  }, [ads.length]);

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + ads.length) % ads.length);
  }, [ads.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleNext, handlePrevious, onClose]);

  if (!ads || ads.length === 0) return null;

  const currentAd = ads[currentIndex];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fadeIn"
      onClick={(e) => {
        // Close when clicking outside the ad content
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="relative max-w-4xl w-full">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 bg-white text-gray-800 p-2 rounded-full hover:bg-gray-200 transition-colors shadow-lg z-10"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Ad Content */}
        <div className="bg-white rounded-lg overflow-hidden shadow-2xl">
          {currentAd.adType === "image" ? (
            // Image-based ad
            <>
              <div className="relative">
                <img
                  src={currentAd.imageUrl}
                  alt={currentAd.description || "Advertisement"}
                  className="w-full h-auto max-h-[70vh] object-contain"
                />
              </div>
              {currentAd.description && (
                <div className="p-6 bg-gray-50">
                  <p className="text-gray-700 text-center text-lg">
                    {parseMarkdownLinks(currentAd.description)}
                  </p>
                </div>
              )}
            </>
          ) : (
            // Text-based ad
            <div className="p-12 min-h-[300px] flex items-center justify-center">
              <div className="text-center">
                <p className="text-2xl md:text-3xl text-gray-800 leading-relaxed">
                  {parseMarkdownLinks(currentAd.description)}
                </p>
              </div>
            </div>
          )}

          {/* Navigation Arrows (only show if more than one ad) */}
          {ads.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 text-gray-800 p-3 rounded-full hover:bg-opacity-100 transition-all shadow-lg z-20"
                aria-label="Previous ad"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <button
                onClick={handleNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 text-gray-800 p-3 rounded-full hover:bg-opacity-100 transition-all shadow-lg z-20"
                aria-label="Next ad"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in;
        }
      `}</style>
    </div>
  );
};

export default HomePageAdCarousel;
